'use client'

import { useAuthState } from '@/app/providers/auth'
import { useLanguage } from '@/components/Link'
import { useAppStateStore } from '@/components/page-specific/catering/common/store/appStateStore'
import FilterButton from '@/components/page-specific/catering/supplier-menu/components/filter-button'
import { MenuSection } from '@/components/page-specific/catering/supplier-menu/components/menu-section'
import { MobileFiltersBar } from '@/components/page-specific/catering/supplier-menu/components/mobile-filters-bar'
import { PopularItemsCarousel } from '@/components/page-specific/catering/supplier-menu/components/popular-items'
import { ShoppingCart } from '@/components/page-specific/catering/supplier-menu/components/shopping-cart'
import { SearchAndFilterMenu } from '@/components/page-specific/catering/supplier-menu/components/sidebar-filter'
import { useMenuPageState } from '@/components/page-specific/catering/supplier-menu/states/menu-page'
import { useMenuPageVendor } from '@/components/page-specific/catering/supplier-menu/states/useMenuPageVendor'
import type {
  VendorMenuCategoriesQueryData,
  VendorMenuFilterStats,
} from '@/components/page-specific/catering/supplier-menu/states/vendorMenuQueries'
import { SidebarShell } from '@/components/ui/SidebarShell'
import type { GetVendorAvailabilityListQuery, GetVendorQuery } from '@/generated/graphql'
import { getMenuViewSource, getMenuViewSourceLabel } from '@/helpers/menuViewSource'
import {
  getDeliveryDetailsForMixpanel,
  useMixPanel,
  useMixpanelInsertIdAppRouter,
} from '@/helpers/mixpanelConfig'
import { setMixpanelItemSearchTerm } from '@/helpers/mixpanelItemSearchAttribution'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'
import TuneIcon from '@mui/icons-material/Tune'
import { Box, Container, Divider, Drawer, Grid } from '@mui/material'

import { useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

import type { LargeMenuOptimizations } from '../page'
import { BASKET_COLUMN_WIDTH } from './basketColumnWidth'

type Props = {
  vendorDetails: NonNullable<GetVendorQuery['vendor']>
  categories: VendorMenuCategoriesQueryData['vendorMenuCategories']
  initialFilterStats?: VendorMenuFilterStats
  initialItemData: GetVendorAvailabilityListQuery
  largeMenuOptimizations: LargeMenuOptimizations
}

export function SidebarAndMenuClient({
  vendorDetails,
  initialItemData,
  largeMenuOptimizations,
}: Props) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const language = useLanguage()
  const authState = useAuthState()
  const searchParams = useSearchParams()
  const mixPanel = useMixPanel()
  const tenant = useTenant()
  const { userState } = useAppStateStore()

  const { vendor } = useMenuPageVendor({ vendorDetails, language })
  const vendorIdForTracking = vendor?.id ?? vendorDetails?.id

  // Mixpanel tracking
  const mixPanelIndividualEvents = useFeatureFlag(SPLITS.MIX_PANEL_INDIVIDUAL_EVENTS)
  const mixPanelEventKey = `mixpanel-menu-viewed-${vendorIdForTracking ?? 'unknown'}`
  const hasTrackedMixPanelRef = useRef(false)
  const mixPanelInsertIdRef = useMixpanelInsertIdAppRouter(mixPanelEventKey)
  const canUseMixpanel = mixPanel?.checkCanUse?.()

  useEffect(() => {
    const source = getMenuViewSource(searchParams)
    const itemSearchTerm = searchParams?.get('itemSearchTerm')
    if (source !== 'item-search' || !itemSearchTerm) return
    setMixpanelItemSearchTerm(itemSearchTerm)
  }, [searchParams])

  useEffect(() => {
    try {
      if (
        !mixPanelIndividualEvents ||
        hasTrackedMixPanelRef.current ||
        typeof window === 'undefined' ||
        !mixPanel ||
        !canUseMixpanel ||
        !tenant ||
        !vendor ||
        !vendorIdForTracking ||
        authState.type === 'loading' ||
        !mixPanelInsertIdRef.current
      ) {
        return
      }

      const deliveryDetails = getDeliveryDetailsForMixpanel(userState)
      const source = getMenuViewSource(searchParams)
      mixPanel.track('Menu Viewed', {
        name: 'Menu Viewed',
        ...deliveryDetails,
        supplierType: vendor.isPantry ? 'Pantry' : 'Catering',
        customerAccount:
          authState.type === 'authenticated'
            ? `${authState.user?.id}-${authState.user.account?.name}`
            : 'guest',
        date: userState.date ?? null,
        headCount: userState.headCount ?? null,
        tenant: tenant?.name,
        vendor: vendor.companyName,
        vendorId: vendorIdForTracking,
        source: getMenuViewSourceLabel(source),
        $insert_id: mixPanelInsertIdRef.current,
      })

      hasTrackedMixPanelRef.current = true
    } catch (e) {
      console.error('Mixpanel tracking failed', e)
    }
  }, [
    mixPanel,
    mixPanelIndividualEvents,
    tenant,
    authState,
    vendor,
    vendorIdForTracking,
    userState,
    searchParams,
    mixPanelInsertIdRef,
    canUseMixpanel,
  ])

  const menuItemsState = useMenuPageState({
    vendor: {
      id: vendor?.id ?? '',
      permalink: vendor?.permalink ?? '',
      categories: vendor?.categories ?? [],
      locations: vendor?.locations ?? [],
    },
    itemData: initialItemData,
    useServerSideItems: true,
    authState,
  })

  const availability = menuItemsState.type === 'loaded' ? menuItemsState.availability : null
  const filtersLoading =
    menuItemsState.type === 'loaded' ? menuItemsState.reloading : menuItemsState.type === 'loading'

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  if (!vendor) return null

  return (
    <>
      <Box sx={{ gridArea: 'sidebar', display: { xs: 'none', lg: 'block' } }}>
        <SidebarShell ariaLabel="Supplier menu filters" width={280} stickyOffset={0}>
          <Box sx={{ flex: 1, minHeight: 0, p: 2, display: 'flex', flexDirection: 'column' }}>
            <SearchAndFilterMenu
              filterCategories={availability?.filterCategories || []}
              loading={filtersLoading}
              filterSections={availability?.filterSections}
            />
          </Box>
        </SidebarShell>
      </Box>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: '80vw',
          },
        }}>
        <Box padding={2}>
          <SearchAndFilterMenu
            filterCategories={availability?.filterCategories || []}
            loading={filtersLoading}
            filterSections={availability?.filterSections}
            hideFilterButton={
              <>
                <Box
                  sx={{
                    display: 'flex',
                    pb: 1,
                    backgroundColor: 'background.paper',
                  }}>
                  <FilterButton
                    onClick={handleDrawerToggle}
                    startIcon={<TuneIcon />}
                    variant="white"
                    sx={{
                      borderRadius: 4,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                    }}>
                    Hide Filters
                  </FilterButton>
                </Box>
                <Divider />
              </>
            }
          />
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          gridArea: 'content',
          minWidth: 0,
          p: { xs: 2, md: 2 },
        }}>
        <Container disableGutters sx={{ maxWidth: { xs: 'lg', xl: 'xl' }, ml: 0 }}>
          <MobileFiltersBar onFilterClick={handleDrawerToggle} />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 'grow' }} sx={{ minWidth: 0 }}>
              {vendor && availability && (
                <PopularItemsCarousel
                  vendor={vendor}
                  visibleItems={availability.items}
                  popularItemRefs={vendorDetails.popularItems ?? []}
                />
              )}
              <MenuSection
                vendor={vendor}
                menuState={menuItemsState}
                largeMenuOptimizations={largeMenuOptimizations}
              />
            </Grid>
            <Grid
              size={{ xs: 12, md: 'auto' }}
              sx={{
                width: BASKET_COLUMN_WIDTH,
                position: { md: 'sticky' },
                top: { md: '80px' },
                alignSelf: { md: 'flex-start' },
                height: { md: 'fit-content' },
                maxHeight: { md: 'calc(100vh - 100px)' },
                overflow: { md: 'auto' },
                scrollbarWidth: 'none',
              }}>
              <ShoppingCart vendor={vendor} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  )
}
